import React, { useRef, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { animated, useSpring } from '@react-spring/web';
import * as AppPropTypes from '../../lib/PropTypes';
import ContactLink from './ContactLink';
import ContactCta from './ContactCta';
import Html from './Html';
import Address from './Address';
import Detector from './Detector';
import { getImageAtSize } from '../../lib/utils';

import styles from '../../../styles/partials/contact-block.module.scss';

const propTypes = {
    contact: AppPropTypes.contact.isRequired,
    invertLinks: PropTypes.bool,
    className: PropTypes.string,
};

const defaultProps = {
    invertLinks: false,
    className: null,
};

const ContactBlock = ({ contact, invertLinks, className }) => {
    const {
        textColor,
        backgroundColor,
        backgroundImage = null,
        links = [],
        title = null,
        description = null,
        address = null,
        linksText = null,
        cta = null,
    } = contact || {};

    const [visible, setVisible] = useState(false);
    const bgImageRef = useRef(null);

    const [{ transform, opacity }, api] = useSpring(() => ({
        transform: 'translateY(100%)',
        opacity: 0,
        config: { friction: 20, tension: 100, mass: 1 },
    }));

    useEffect(() => {
        if (visible) {
            api.start({
                transform: `translateY(0)`,
                opacity: 0.5,
            });
        } else {
            api.start({
                transform: `translateY(100%)`,
                opacity: 0,
            });
        }
    }, [visible]);

    const finalImage = getImageAtSize(backgroundImage, 'medium');

    const finalLinks = useMemo(() => {
        if(invertLinks) {
            return (links || []).reverse();
        }
        return (links || []);
    }, [links, invertLinks]);

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.visible]: visible,
                    [className]: className !== null,
                },
            ])}
            style={{
                backgroundColor,
            }}
        >
            <Detector rootMargin="-20%" onChange={(v) => setVisible(v)}>
                <animated.div
                    className={styles.animatedBg}
                    style={{
                        transform,
                        opacity,
                    }}
                >
                    <div
                        className={styles.backgroundImageContainer}
                        ref={bgImageRef}
                        style={{
                            backgroundImage: `url(${finalImage.url})`,
                        }}
                    />
                </animated.div>
                <div
                    className={styles.inner}
                    style={{
                        color: textColor,
                    }}
                >
                    <div className={styles.left}>
                        {title !== null ? (
                            <h3
                                className={classNames([
                                    styles.title,
                                    {
                                        [styles.long]: title.length > 10,
                                    },
                                ])}
                            >
                                {title}
                            </h3>
                        ) : null}
                        {description !== null ? (
                            <Html body={description} className={styles.description} />
                        ) : null}
                        {address !== null ? (
                            <Address
                                address={address}
                                className={classNames([styles.description, styles.address])}
                            />
                        ) : null}
                        {cta !== null ? (
                            <div className={styles.ctaContainer}>
                                <ContactCta
                                    cta={cta}
                                    className={classNames([
                                        styles.cta,
                                        {
                                            [styles.isLight]:
                                                backgroundColor === '#FFFC00' ||
                                                backgroundColor === '#FFDCCC',
                                        },
                                    ])}
                                />
                            </div>
                        ) : null}
                    </div>
                    <div className={styles.right}>
                        <ul>
                            {finalLinks.map((l, idx) => (
                                <li key={`contact-link-${idx + 1}`}>
                                    <ContactLink link={l} className={styles.link} />
                                </li>
                            ))}
                        </ul>
                        {linksText !== null ? (
                            <Html
                                body={linksText}
                                className={classNames([styles.description, styles.rightDesc])}
                            />
                        ) : null}
                    </div>
                </div>
            </Detector>
        </div>
    );
};

ContactBlock.defaultProps = defaultProps;
ContactBlock.propTypes = propTypes;

export default ContactBlock;
